import { faClose, faLink } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { SIZE } from "../../../configuration/Sizes"
import type { IPlatformWhatsAppPairingResult } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppPairingResult"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"

const AGENT_NAME_MAX_LENGTH = 10

type Props = {
  agentName: string
  result: IPlatformWhatsAppPairingResult | null
  onAgentNameChange: (value: string) => void
  onGenerate: () => void
  onClose: () => void
}

export const PlatformWhatsAppPairingPopup = ({ agentName, result, onAgentNameChange, onGenerate, onClose }: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall title={t("pairing.title")} icon={faLink} iconColor={THEME.colors.iconBGColor}>
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppPairingPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <div className="grid gap-4 p-3">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
                <div>
                  <a
                    href={API_ROUTES.WHATSAPP_AUTOMATION.DOWNLOAD_AGENT}
                    className="block rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-center text-sm font-bold text-sky-700 transition-all duration-200 hover:border-sky-300 hover:bg-sky-100"
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppPairingPopup.link.download"
                  >
                    {t("pairing.download")}
                  </a>
                </div>
              </Animate>

              {result ? (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
                    <p className="text-sm font-bold text-emerald-900">{t("pairing.code")}</p>

                    <p className="my-3 font-mono text-4xl font-black tracking-[0.3em] text-emerald-700">
                      {result.pairingCode}
                    </p>

                    <p className="text-xs text-emerald-800">{t("pairing.instructions", { agentId: result.agentId })}</p>
                  </div>
                </Animate>
              ) : (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                  <div className="grid gap-3">
                    <InputField
                      type="text"
                      value={agentName}
                      maxLength={AGENT_NAME_MAX_LENGTH}
                      onChange={(event) => onAgentNameChange(event.target.value.slice(0, AGENT_NAME_MAX_LENGTH))}
                      placeholder={t("pairing.name")}
                      required
                      animationDuration="1.2s"
                      descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppPairingPopup.input.name"
                    />

                    <Button
                      bgColor={THEME.colors.buttonBGSaveColor}
                      label={t("pairing.generate")}
                      disabled={!agentName.trim() || agentName.trim().length > AGENT_NAME_MAX_LENGTH}
                      onClick={onGenerate}
                      animationDuration="1.3s"
                      descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppPairingPopup.button.generate"
                    />
                  </div>
                </Animate>
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
